import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';

import { QUESTION_TYPES } from '@api/instant_survey';

import LogicOptions from '../../../../../../../../../QuestionLogic/components/LogicOptions';
import NumericLogicInput from '../../../../../../../../../QuestionLogic/components/NumericLogicInput';
import QuestionLogicOptions from '../../../../../../../../../QuestionLogic/components/QuestionLogicOptions';
import {
  answerOperators,
  availableLogicOptions,
  instantSurveyGroupRefUrlVariable,
  LOGIC_OPTIONS,
  numericLogicOperators,
} from '../../../../../../../../../QuestionLogic/utilities/constants';
import { SurveyContext } from '../../../../../../../../../../../../utilities/context';

import styles from './_index.module.scss';

const OptionLogicRow = ({
  answerId,
  firstErrorRef,
  index,
  isCompleted,
  questionId,
  questionIndex,
  questionList,
  questionLogic,
  setQuestionLogic,
}) => {
  const { survey } = useContext(SurveyContext);
  const getLogicType = () => {
    const logicRow = questionLogic[questionId].option[answerId].logic[index];
    // The first question can only use quota group logic
    return logicRow?.url_variable === instantSurveyGroupRefUrlVariable ||
      (questionIndex === 0 && !isCompleted)
      ? LOGIC_OPTIONS.QUOTA_GROUP.value
      : LOGIC_OPTIONS.ANSWER.value;
  };

  const [logicType, setLogicType] = useState(getLogicType);

  useEffect(() => {
    setLogicType(getLogicType());
  }, [
    index,
    instantSurveyGroupRefUrlVariable,
    isCompleted,
    questionId,
    answerId,
    questionLogic,
    questionIndex,
  ]);

  const filteredQuestionList = questionList.filter(
    (question) => question.type !== QUESTION_TYPES.INSTRUCTIONS.value
  );
  const quotaGroupOptions = survey.groups
    .filter(({ id }) => id !== null && id !== undefined)
    .map((group) => ({
      id: group.id,
      text: group.name,
    }));
  const operatorOptions =
    questionLogic[questionId].option[answerId].logic[index].question?.type ===
    QUESTION_TYPES.NUMERIC.value
      ? numericLogicOperators
      : answerOperators;
  const isQuotaGroupSelectionValid = () => {
    const groupIds = quotaGroupOptions.map(({ id }) => String(id));
    const logicQuotaGroupIds = (
      questionLogic[questionId]?.option?.[answerId]?.logic?.[index]?.values || []
    ).map(String);
    return logicQuotaGroupIds.every((id) => groupIds.includes(id));
  };

  const updateAnswers = (answers) => {
    const updatedLogic = cloneDeep(questionLogic);
    updatedLogic[questionId].option[answerId].logic[index].answers = answers;
    updatedLogic[questionId].option[answerId].logic[index].hasAnswerError = false;
    updatedLogic[questionId].option[answerId].logic[index].isFirstAnswerError = false;
    setQuestionLogic(updatedLogic);
  };

  const updateComparisonValue = (value) => {
    const updatedLogic = cloneDeep(questionLogic);
    updatedLogic[questionId].option[answerId].logic[index].comparison_value = value;
    updatedLogic[questionId].option[answerId].logic[index].hasAnswerError = false;
    updatedLogic[questionId].option[answerId].logic[index].isFirstAnswerError = false;
    setQuestionLogic(updatedLogic);
  };

  const updateLogicType = (newLogicType) => {
    setLogicType(newLogicType);
    const updatedLogic = cloneDeep(questionLogic);
    if (newLogicType === LOGIC_OPTIONS.QUOTA_GROUP.value) {
      updatedLogic[questionId].option[answerId].logic[index] = {
        url_variable: instantSurveyGroupRefUrlVariable,
        values: [],
      };
    } else {
      updatedLogic[questionId].option[answerId].logic[index] = {
        answers: [],
        question: null,
      };
    }
    updatedLogic[questionId].option[answerId].logic[index].operator = answerOperators[0];
    updatedLogic[questionId].option[answerId].logic[index].hasAnswerError = false;
    setQuestionLogic(updatedLogic);
  };

  const updateOperator = (operator) => {
    const updatedLogic = cloneDeep(questionLogic);
    updatedLogic[questionId].option[answerId].logic[index].operator = operator;
    setQuestionLogic(updatedLogic);
  };

  const updateQuestion = (question) => {
    const updatedLogic = cloneDeep(questionLogic);
    delete updatedLogic[questionId].option[answerId].logic[index].url_variable;
    delete updatedLogic[questionId].option[answerId].logic[index].values;
    delete updatedLogic[questionId].option[answerId].logic[index].comparison_value;
    updatedLogic[questionId].option[answerId].logic[index].answers = [];
    updatedLogic[questionId].option[answerId].logic[index].question = question;
    updatedLogic[questionId].option[answerId].logic[index].hasQuestionError = false;
    updatedLogic[questionId].option[answerId].logic[index].isFirstQuestionError = false;

    if (question?.type === QUESTION_TYPES.NUMERIC.value) {
      updatedLogic[questionId].option[answerId].logic[index].operator = numericLogicOperators[0];
    } else {
      updatedLogic[questionId].option[answerId].logic[index].operator = answerOperators[0];
    }

    setQuestionLogic(updatedLogic);
  };

  const updateQuotaGroups = (groups) => {
    const updatedLogic = cloneDeep(questionLogic);
    const groupIds = groups.map(({ id }) => String(id));
    delete updatedLogic[questionId].option[answerId].logic[index].answers;
    delete updatedLogic[questionId].option[answerId].logic[index].question;
    delete updatedLogic[questionId].option[answerId].logic[index].hasQuestionError;
    delete updatedLogic[questionId].option[answerId].logic[index].isFirstQuestionError;
    updatedLogic[questionId].option[answerId].logic[index].hasAnswerError = false;
    updatedLogic[questionId].option[answerId].logic[index].isFirstAnswerError = false;
    updatedLogic[questionId].option[answerId].logic[index].url_variable =
      instantSurveyGroupRefUrlVariable;
    updatedLogic[questionId].option[answerId].logic[index].values = groupIds;
    setQuestionLogic(updatedLogic);
  };

  return (
    <div className={styles['option-logic-row']}>
      <span>
        The{' '}
        <LogicOptions
          isCompleted={isCompleted}
          logicOptions={
            isCompleted || questionIndex !== 0
              ? availableLogicOptions
              : availableLogicOptions.slice(1)
          }
          optionsLabel="Type"
          setValue={updateLogicType}
          value={logicType}
        />
      </span>
      {logicType === LOGIC_OPTIONS.ANSWER.value ? (
        <>
          <span>&nbsp;to&nbsp;</span>
          <QuestionLogicOptions
            firstErrorRef={firstErrorRef}
            isCompleted={isCompleted}
            isError={questionLogic[questionId].option[answerId].logic[index].hasQuestionError}
            isFocused={questionLogic[questionId].option[answerId].logic[index].isFirstQuestionError}
            isQuestion
            isRequired
            options={filteredQuestionList}
            optionsLabel="Question"
            optionsType="radio"
            setValue={updateQuestion}
            value={questionLogic[questionId].option[answerId].logic[index].question}
          />
          {questionLogic[questionId].option[answerId].logic[index].question?.id ? (
            <>
              <span>&nbsp;is&nbsp;</span>
              <QuestionLogicOptions
                isCompleted={isCompleted}
                options={operatorOptions}
                optionsLabel="Operator"
                optionsType="radio"
                setValue={updateOperator}
                value={questionLogic[questionId].option[answerId].logic[index].operator}
              />
              :
            </>
          ) : (
            <span>...</span>
          )}
          {questionLogic[questionId].option[answerId].logic[index].question?.id && (
            <>
              {questionLogic[questionId].option[answerId].logic[index].question.type ===
              QUESTION_TYPES.NUMERIC.value ? (
                <NumericLogicInput
                  firstErrorRef={firstErrorRef}
                  isError={questionLogic[questionId].option[answerId].logic[index].hasAnswerError}
                  isFocused={
                    questionLogic[questionId].option[answerId].logic[index].isFirstAnswerError
                  }
                  question={questionLogic[questionId].option[answerId].logic[index].question}
                  rootCloseEvent="mousedown"
                  setValue={updateComparisonValue}
                  value={
                    questionLogic[questionId].option[answerId].logic[index].comparison_value ?? ''
                  }
                />
              ) : (
                <QuestionLogicOptions
                  firstErrorRef={firstErrorRef}
                  isCompleted={isCompleted}
                  isError={questionLogic[questionId].option[answerId].logic[index].hasAnswerError}
                  isFocused={
                    questionLogic[questionId].option[answerId].logic[index].isFirstAnswerError
                  }
                  isRequired
                  isSkip
                  options={
                    questionLogic[questionId].option[answerId].logic[index].question?.answers
                  }
                  optionsLabel="Answer Options"
                  optionsType="checkbox"
                  setValue={updateAnswers}
                  value={questionLogic[questionId].option[answerId].logic[index].answers}
                />
              )}
            </>
          )}
        </>
      ) : (
        <>
          <span>&nbsp;is&nbsp;</span>
          <QuestionLogicOptions
            isCompleted={isCompleted}
            options={answerOperators}
            optionsLabel="Operator"
            optionsType="radio"
            setValue={updateOperator}
            value={questionLogic[questionId].option[answerId].logic[index].operator}
          />
          <span>&nbsp;:&nbsp;</span>
          <QuestionLogicOptions
            firstErrorRef={firstErrorRef}
            isCompleted={isCompleted}
            isError={
              !isQuotaGroupSelectionValid() ||
              questionLogic[questionId].option[answerId].logic[index].hasAnswerError
            }
            isFocused={questionLogic[questionId].option[answerId].logic[index].isFirstAnswerError}
            isQuotaGroup
            isQuotaGroupSelectionValid={isQuotaGroupSelectionValid()}
            options={quotaGroupOptions}
            optionsLabel="Quota Group"
            optionsType="checkbox"
            setValue={updateQuotaGroups}
            value={quotaGroupOptions.filter(({ id }) =>
              (questionLogic[questionId].option[answerId].logic[index].values || []).includes(
                String(id)
              )
            )}
          />
        </>
      )}
    </div>
  );
};

OptionLogicRow.propTypes = {
  answerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  firstErrorRef: PropTypes.object,
  index: PropTypes.number.isRequired,
  isCompleted: PropTypes.bool,
  questionId: PropTypes.number.isRequired,
  questionIndex: PropTypes.number,
  questionList: PropTypes.array.isRequired,
  questionLogic: PropTypes.object.isRequired,
  setQuestionLogic: PropTypes.func.isRequired,
};

export default OptionLogicRow;
